import React, { useLayoutEffect, useMemo, useRef, useState } from "react";

import { TFunction, formatNumericCurrency } from "utils";

import { CurrencyCode } from "consts";

import { Payment, SingleFieldOutcomes } from "models";

import { AlertBanner, ErrorBoundary, ExtraInfo, Flex, Icon, Link, PayoutAmount, Tag, Tooltip, Trans, Typography } from "components";

import { PayoutProgressBar, buildCo2ProgressBarProps, buildPayoutProgressBarProps } from "pages/app/field-timeline/components/PayoutImpactProgressBar";
import { PayoutContextDrawer } from "pages/app/field-timeline/components/payout-context-drawer/PayoutContextDrawer";
import { PayoutStatus, capPayoutAtEligible, formatPerHaValue } from "pages/app/field-timeline/utils/PayoutUtils";

export type ExpectedPayoutsCardProps = {
  totalPayoutMin: number;
  totalPayoutMax: number;
  eligiblePayout: number;
  previousEligiblePayout?: number;
  /** Early data entry bonus amount (shown as second tag when > 0) */
  bonusAmount?: number;
  totalEnrolledHa: number;
  co2Actual: number;
  co2Max: number | null;
  co2MaxPayoutImpact: number | null;
  co2Baseline: number;
  co2BaselineYear?: number;
  payoutStatus: PayoutStatus | null;
  isNegative: boolean;
  isFutureYear: boolean;
  hasDataEntry: boolean;
  isPastPoP: boolean;
  minPotential?: number;
  maxPotential?: number;
  currency?: CurrencyCode;
  t: TFunction;
  fieldsContributing: number;
  fieldsWithNonFatalErrors: number;
  totalFields: number;
  selectedYear: number;
  reportingYear?: number;
  monitoringYears?: number[];
  allFieldOutcomes?: SingleFieldOutcomes[];
  baselineFootprints?: Record<number, number>;
  farmPayments?: Payment[];
  onCongratulationsBannerClose?: () => void;
  onOpenAllotmentInfo?: () => void;
  isAllotmentAcknowledged?: boolean;
};

const BADGE_MIN_WIDTH = 120;
const ICON_BADGE_WIDTH = 28;
const AMOUNT_ROW_GAP_ALLOWANCE = 16;

//  Status Badge — TODO: could be of help for the next tickets

const statusBadgeConfig: Record<PayoutStatus, { colour: "green" | "red" | "grey" | "yellow"; icon?: string }> = {
  onTrack: { colour: "yellow", icon: "tabler/circle-check" },
  keepRolling: { colour: "yellow", icon: "tabler/arrow-up-right-circle" },
  needsAttention: { colour: "yellow", icon: "tabler/alert-triangle" },
  noPayouts: { colour: "yellow", icon: "tabler/alert-triangle" },
  awaitingData: { colour: "yellow", icon: "tabler/loader" },
  inVerification: { colour: "yellow", icon: "tabler/lock" },
  verified: { colour: "yellow", icon: "tabler/lock" },
};

const StatusBadge = ({ status, t, maxWidth }: { status: PayoutStatus; t: TFunction; maxWidth?: number }) => {
  const config = statusBadgeConfig[status];
  const statusText = t(`impactPanel.payoutImpact.status.${status}`);
  const isTruncated = maxWidth !== undefined;

  const badge = (
    <Tag
      className={`expected-payouts-status-badge${isTruncated ? " expected-payouts-status-badge--truncated" : ""}`}
      style={isTruncated ? { maxWidth } : undefined}
      colour={config.colour}
      text={
        <Flex align="center" gap="0.25rem">
          {config.icon && (
            <span className="expected-payouts-status-icon">
              <Icon iconName={config.icon} size="s" />
            </span>
          )}
          <span className="expected-payouts-status-text">{statusText}</span>
        </Flex>
      }
      size="small"
      dataTestId={`payout-status-badge-${status}`}
    />
  );

  if (isTruncated) {
    return (
      <Tooltip title={statusText} overlayClassName="expected-payouts-status-tooltip" color="#fff" styles={{ body: { color: "#100c06" } }}>
        {badge}
      </Tooltip>
    );
  }

  return badge;
};

//  Banners

const CongratulationsBanner = ({
  previousEligible,
  currentEligible,
  currency,
  t,
  onClose,
}: {
  previousEligible: number;
  currentEligible: number;
  currency?: CurrencyCode;
  t: TFunction;
  onClose?: () => void;
}) => {
  const fmt = (v: number) => formatNumericCurrency(v, { decimals: 0, currency });

  if (previousEligible === 0 && currentEligible > 0) {
    return (
      <AlertBanner
        className="expected-payouts-congratulations"
        dataTestId="expected-payouts-congratulations-banner"
        iconName="tabler/info-circle"
        title={t("impactPanel.payoutImpact.congratulations")}
        type="#FAFDDB" // yellow-100
        description={
          <Typography variant="paragraph-xs">{t("impactPanel.payoutImpact.eligibleIncreasedFromZero", { amount: fmt(currentEligible) })}</Typography>
        }
        closeable
        onClose={onClose}
        iconAlign="start"
        gap={4}
        iconSize={24}
        headingSize="h6"
        descriptionSize="paragraph-s"
      />
    );
  }

  if (previousEligible > 0 && currentEligible > previousEligible) {
    return (
      <AlertBanner
        className="expected-payouts-congratulations"
        dataTestId="expected-payouts-congratulations-banner"
        iconName="tabler/info-circle"
        title={t("impactPanel.payoutImpact.congratulations")}
        description={
          <Typography variant="paragraph-xs">
            {t("impactPanel.payoutImpact.eligibleIncreasedPrefix")}{" "}
            <strong>
              {t("impactPanel.payoutImpact.eligibleIncreasedRange", {
                from: fmt(previousEligible),
                to: fmt(currentEligible),
              })}
            </strong>
            {", "}
            {t("impactPanel.payoutImpact.eligibleIncreasedSuffix")}
          </Typography>
        }
        type="#FAFDDB" // yellow-100
        closeable
        onClose={onClose}
        iconAlign="start"
        gap={4}
        iconSize={24}
        headingSize="h6"
        descriptionSize="paragraph-s"
      />
    );
  }

  return null;
};

const NoAllotmentBanner = ({ t, onLearnMoreClick }: { t: TFunction; onLearnMoreClick?: () => void }) => (
  <AlertBanner
    className="expected-payouts-info"
    dataTestId="expected-payouts-no-allotment-banner"
    iconName="tabler/info-circle"
    description={t("impactPanel.payoutImpact.noPayoutUnlockedDescription")}
    type="#E8E8E8"
    iconAlign="start"
    gap={4}
    iconSize={16}
    descriptionSize="paragraph-s"
    link
    linkText={t("impactPanel.payoutImpact.learnMore")}
    onClick={onLearnMoreClick}
  />
);

const InVerificationBanner = ({ t }: { t: TFunction }) => (
  <AlertBanner
    className="expected-payouts-info"
    dataTestId="expected-payouts-in-verification-banner"
    iconName="tabler/info-circle"
    title={t("impactPanel.payoutImpact.inVerificationInfoTitle")}
    description={t("impactPanel.payoutImpact.inVerificationInfoDescription")}
    type="#E8E8E8"
    iconAlign="start"
    gap={4}
    iconSize={24}
    headingSize="h6"
    descriptionSize="paragraph-s"
  />
);

const VerifiedBanner = ({ t }: { t: TFunction }) => (
  <AlertBanner
    className="expected-payouts-info"
    dataTestId="expected-payouts-verified-banner"
    iconName="tabler/circle-check"
    title={t("impactPanel.payoutImpact.verifiedInfoTitle")}
    description={t("impactPanel.payoutImpact.verifiedInfoDescription")}
    type="#E8E8E8"
    iconAlign="start"
    gap={4}
    iconSize={24}
    headingSize="h6"
    descriptionSize="paragraph-s"
  />
);

const NegativePayoutBanner = ({ t, onClick }: { t: TFunction; onClick: () => void }) => (
  <AlertBanner
    className="expected-payouts-warning"
    dataTestId="expected-payouts-negative-banner"
    iconName="tabler/info-circle"
    description={t("impactPanel.payoutImpact.negativePayoutInfo")}
    type="#F1F6FF" // blue-100
    link
    linkText={t("impactPanel.payoutImpact.learnMore")}
    onClick={onClick}
    iconAlign="start"
    gap={4}
    iconSize={24}
    descriptionSize="paragraph-s"
  />
);

const FutureYearBanner = ({ t, onClick }: { t: TFunction; onClick: () => void }) => (
  <AlertBanner
    className="expected-payouts-warning"
    dataTestId="expected-payouts-future-banner"
    iconName="tabler/alert-triangle"
    title={t("impactPanel.payoutImpact.futureYearInfoTitle")}
    description={t("impactPanel.payoutImpact.futureYearInfoDescription")}
    type="#E8E8E8" // grey-300
    iconAlign="start"
    gap={4}
    iconSize={24}
    headingSize="h6"
    link
    linkText={t("impactPanel.payoutImpact.learnMore")}
    onClick={onClick}
    descriptionSize="paragraph-s"
  />
);

const NonModelableCropsYellowBanner = ({ t }: { t: TFunction }) => (
  <AlertBanner
    className="expected-payouts-warning"
    dataTestId="expected-payouts-non-modelable-crops-banner"
    description={t("impactPanel.payoutImpact.nonModelableCropsCard.descriptionAll")}
    iconAlign="start"
    iconName="tabler/info-circle"
    iconSize={16}
    title={t("impactPanel.payoutImpact.nonModelableCropsCard.title")}
    type="info-highlighted"
  />
);

const NonModelableCropsGreyBanner = ({ t }: { t: TFunction }) => (
  <AlertBanner
    className="expected-payouts-warning"
    dataTestId="expected-payouts-non-modelable-crops-banner"
    description={t("impactPanel.payoutImpact.nonModelableCropsCard.descriptionSome")}
    iconAlign="start"
    iconName="tabler/info-circle"
    iconSize={16}
    title={t("impactPanel.payoutImpact.nonModelableCropsCard.title")}
    type="info"
  />
);

export const ExpectedPayoutsCard = ({
  totalPayoutMin,
  totalPayoutMax,
  eligiblePayout,
  previousEligiblePayout: _previousEligiblePayout = 0,
  bonusAmount = 0,
  totalEnrolledHa,
  co2Actual,
  co2Max,
  co2MaxPayoutImpact,
  co2Baseline,
  co2BaselineYear,
  payoutStatus,
  isNegative,
  isFutureYear,
  hasDataEntry,
  isPastPoP,
  minPotential,
  maxPotential,
  currency,
  t,
  fieldsContributing,
  fieldsWithNonFatalErrors,
  totalFields,
  selectedYear,
  reportingYear,
  monitoringYears = [],
  allFieldOutcomes = [],
  baselineFootprints = {},
  farmPayments = [],
  isAllotmentAcknowledged,
  onCongratulationsBannerClose,
  onOpenAllotmentInfo,
}: ExpectedPayoutsCardProps) => {
  const [showContextDrawer, setShowContextDrawer] = useState(false);
  const [badgeMode, setBadgeMode] = useState<"full" | "truncated" | "stacked">("full");
  const [truncatedWidth, setTruncatedWidth] = useState(0);
  const amountRowRef = useRef<HTMLDivElement>(null);
  const badgeNaturalWidth = useRef(BADGE_MIN_WIDTH);
  const badgeNaturalWidthCaptured = useRef(false);

  const farmPaymentForSelectedYear = useMemo(() => farmPayments?.find((payment) => payment.year === selectedYear), [farmPayments, selectedYear]);
  const isVerificationPending = farmPaymentForSelectedYear?.status === "inVerification";
  const isVerificationCompleted = farmPaymentForSelectedYear?.status === "paid";

  let pastPayout = 0;

  if (isVerificationPending) pastPayout = farmPaymentForSelectedYear?.amount;

  if (isVerificationCompleted) pastPayout = farmPaymentForSelectedYear?.rawAmount;

  const cappedTotalPayoutMin = capPayoutAtEligible(totalPayoutMin, eligiblePayout);

  const pastPayoutOrCappedTotalPayoutMin = isPastPoP ? pastPayout : cappedTotalPayoutMin;

  const hasNoAllotment = !isFutureYear && !isPastPoP && eligiblePayout === 0 && !isNegative;

  const perHaLabel = formatPerHaValue(pastPayoutOrCappedTotalPayoutMin, totalEnrolledHa, currency);

  useLayoutEffect(() => {
    const row = amountRowRef.current;
    if (!row) return;

    const handleResize = () => {
      const bigNumberEl = row.querySelector<HTMLElement>(".expected-payouts-big-number");
      const perHaEl = row.querySelector<HTMLElement>(".expected-payouts-per-ha");
      if (!bigNumberEl || !perHaEl) return;

      const fullBadgeEl = row.querySelector<HTMLElement>(".expected-payouts-status-badge:not(.expected-payouts-status-badge--truncated)");
      if (fullBadgeEl && !badgeNaturalWidthCaptured.current) {
        badgeNaturalWidth.current = fullBadgeEl.offsetWidth;
        badgeNaturalWidthCaptured.current = true;
      }

      const available =
        row.getBoundingClientRect().width - bigNumberEl.getBoundingClientRect().width - perHaEl.getBoundingClientRect().width - AMOUNT_ROW_GAP_ALLOWANCE;

      if (available >= badgeNaturalWidth.current) {
        setBadgeMode("full");
      } else if (available >= ICON_BADGE_WIDTH) {
        setBadgeMode("truncated");
        setTruncatedWidth(available);
      } else {
        setBadgeMode("stacked");
      }
    };

    handleResize();

    const observer = new ResizeObserver(handleResize);
    observer.observe(row);

    return () => observer.disconnect();
  }, [pastPayoutOrCappedTotalPayoutMin, hasNoAllotment, perHaLabel, payoutStatus]);

  // Link component for "Learn more" in tooltips
  const TooltipLearnMoreLink = <Link type="primary-link" className="tooltip-learn-more-link" onClick={() => setShowContextDrawer(true)} />;

  // CO₂ section title: "Impact" when there is actual data, "CO₂ Impact" when zero/future
  const co2Title = co2Actual !== 0 && !isFutureYear ? t("impactPanel.payoutImpact.impactTitle") : t("impactPanel.payoutImpact.co2ImpactTitle");

  // Build payout bar props
  const payoutBarProps = useMemo(
    () =>
      buildPayoutProgressBarProps({
        actual: pastPayoutOrCappedTotalPayoutMin,
        eligible: eligiblePayout,
        maxValue: hasDataEntry ? totalPayoutMax : null,
        isNegative,
        isFutureYear,
        hasDataEntry,
        isPastPoP,
        minPotential,
        maxPotential,
        bonusAmount,
        currency,
        t,
      }),
    [
      pastPayoutOrCappedTotalPayoutMin,
      eligiblePayout,
      hasDataEntry,
      totalPayoutMax,
      isNegative,
      isFutureYear,
      isPastPoP,
      minPotential,
      maxPotential,
      bonusAmount,
      currency,
      t,
    ]
  );

  // Build CO₂ bar props
  const co2BarProps = useMemo(
    () =>
      buildCo2ProgressBarProps({
        actual: co2Actual,
        maxPayoutImpact: co2MaxPayoutImpact,
        notEligible: co2Max,
        baseline: co2Baseline,
        baselineYear: co2BaselineYear,
        isNegative: co2Actual < 0, // display the negative co2 bar when co2Actual is negative and not relying on allotment status
        isFutureYear,
        hasDataEntry,
        isPastPoP,
        t,
      }),
    [co2Actual, co2MaxPayoutImpact, co2Max, co2Baseline, co2BaselineYear, isFutureYear, hasDataEntry, isPastPoP, t]
  );

  return (
    <div className="expected-payouts-card" data-testid="expected-payouts-card">
      {/* Header row */}
      <Flex justify="space-between" align="center" className="expected-payouts-header">
        <Flex align="center" gap="0.25rem">
          <Typography variant="paragraph-m" bold>
            {t("impactPanel.payoutImpact.title")}
          </Typography>
          <Tooltip placement="top" title={<Trans i18nKey="fieldTimeline:impactPanel.payoutImpact.tooltip" components={[TooltipLearnMoreLink]} />}>
            <Icon iconName="tabler/info-circle" size="s" className="expected-payouts-info-icon" />
          </Tooltip>
        </Flex>
        {!isPastPoP && (
          <Icon
            iconName="tabler/chevron-right"
            size="s"
            className="expected-payouts-drawer-icon pointer"
            onClick={() => setShowContextDrawer(true)}
            dataTestId="expected-payouts-drawer-icon"
          />
        )}
      </Flex>
      {/* Big number row */}
      <div ref={amountRowRef} className="expected-payouts-amount-row">
        <Flex justify="space-between" align="center" gap="0.5rem">
          <Flex align="center" gap="0.5rem" className="expected-payouts-payout-status-badge-container">
            <PayoutAmount
              amount={pastPayoutOrCappedTotalPayoutMin}
              hasAllotment={!hasNoAllotment}
              currency={currency}
              className={`expected-payouts-big-number ${isNegative ? "negative" : ""}`}
              dataTestId="expected-payouts-big-number"
            />
            {payoutStatus && badgeMode !== "stacked" && (
              <StatusBadge status={payoutStatus} t={t} maxWidth={badgeMode === "truncated" ? truncatedWidth : undefined} />
            )}
          </Flex>
          {!hasNoAllotment && (
            <Typography variant="paragraph-s" data-testid="expected-payouts-per-ha" className="expected-payouts-per-ha">
              {perHaLabel}
            </Typography>
          )}
        </Flex>
        {payoutStatus && badgeMode === "stacked" && (
          <div className="expected-payouts-stacked-badge">
            <StatusBadge status={payoutStatus} t={t} />
          </div>
        )}
      </div>
      {/* Banners */}
      <Flex vertical gap="0.5rem" className="expected-payouts-banners" data-testid="expected-payouts-banners" style={{ marginBottom: "1rem" }}>
        {!isAllotmentAcknowledged && !isPastPoP && (
          <CongratulationsBanner
            previousEligible={_previousEligiblePayout}
            currentEligible={eligiblePayout}
            currency={currency}
            t={t}
            onClose={onCongratulationsBannerClose}
          />
        )}

        {hasNoAllotment && <NoAllotmentBanner t={t} onLearnMoreClick={onOpenAllotmentInfo} />}

        {/* In Verification banner - has to stay on top of negative payout banner */}
        {isVerificationPending && <InVerificationBanner t={t} />}

        {isVerificationCompleted && <VerifiedBanner t={t} />}

        {isFutureYear && (hasDataEntry || isPastPoP) && <FutureYearBanner t={t} onClick={() => setShowContextDrawer(true)} />}

        {isNegative && !isPastPoP && <NegativePayoutBanner t={t} onClick={() => setShowContextDrawer(true)} />}

        {!!fieldsWithNonFatalErrors && fieldsWithNonFatalErrors === totalFields && !isPastPoP && !hasNoAllotment && <NonModelableCropsYellowBanner t={t} />}
        {!!fieldsWithNonFatalErrors && !isPastPoP && !hasNoAllotment && fieldsWithNonFatalErrors !== totalFields && <NonModelableCropsGreyBanner t={t} />}
      </Flex>

      {/* Payout progress bar (hidden when no allotment) */}
      {!hasNoAllotment && (
        <PayoutProgressBar
          title={t("impactPanel.payoutImpact.payoutTitle")}
          tooltipText={<Trans i18nKey="fieldTimeline:impactPanel.payoutImpact.payoutTooltip" components={[TooltipLearnMoreLink]} />}
          valueTagLabel={payoutBarProps.valueTagLabel}
          valueTagVariant={payoutBarProps.valueTagVariant}
          valueTagPositionPct={payoutBarProps.valueTagPositionPct}
          bonusTag={payoutBarProps.bonusTag}
          segments={payoutBarProps.segments}
          divider={payoutBarProps.divider}
          colorVariant="blue"
          ticks={payoutBarProps.ticks}
          dataTestId="payout-bar"
          isLocked={isVerificationPending || isVerificationCompleted}
          isPastPoP={isPastPoP}
        />
      )}
      {/* CO₂ Impact progress bar */}
      <PayoutProgressBar
        title={co2Title}
        tooltipText={<Trans i18nKey="fieldTimeline:impactPanel.payoutImpact.co2Tooltip" components={[TooltipLearnMoreLink]} />}
        valueTagLabel={co2BarProps.valueTagLabel}
        valueTagVariant={co2BarProps.valueTagVariant}
        valueTagPositionPct={co2BarProps.valueTagPositionPct}
        segments={co2BarProps.segments}
        divider={co2BarProps.divider}
        colorVariant="green"
        ticks={co2BarProps.ticks}
        dataTestId="co2-bar"
        isLocked={isVerificationPending || isVerificationCompleted}
        isPastPoP={isPastPoP}
      />
      {/* Enrolled fields estimation */}
      <Flex align="center" gap="0.375rem" className="expected-payouts-estimation" data-testid="expected-payouts-estimation">
        <Icon iconName="tabler/bulb" size="s" className="expected-payouts-estimation-icon" />
        <Typography variant="paragraph-xs">
          {t("impactPanel.payoutImpact.estimationBased", {
            contributing: fieldsContributing,
            total: totalFields,
          })}
          {!!fieldsWithNonFatalErrors && <> {t("impactPanel.payoutImpact.estimationBasedExtension", { number: fieldsWithNonFatalErrors })}</>}
        </Typography>
      </Flex>

      <ErrorBoundary>
        {reportingYear && (
          <PayoutContextDrawer
            visible={showContextDrawer}
            onClose={() => setShowContextDrawer(false)}
            reportingYear={reportingYear}
            monitoringYears={monitoringYears}
            currency={currency}
            actualPayout={pastPayoutOrCappedTotalPayoutMin}
            eligiblePayout={eligiblePayout}
            totalPayoutMax={totalPayoutMax}
            co2Actual={co2Actual}
            co2Baseline={co2Baseline}
            hasDataEntry={hasDataEntry}
            allFieldOutcomes={allFieldOutcomes}
            baselineFootprints={baselineFootprints}
            farmPayments={farmPayments}
            hasNoAllotment={hasNoAllotment}
          />
        )}
      </ErrorBoundary>
      <ExtraInfo setVisible={setShowContextDrawer} type="expected-payouts-context" visible={showContextDrawer} />
    </div>
  );
};
